// react-magnetic-di - temporary comment, for auto dependency injection in tests
import { useCallback, useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { fireOperationalEvent } from '@atlassian/ui-modifications-analytics';

import type { UiModificationsConfigSingleApp } from '../../../../../../../common/types/apps';
import { useCurrentFormState } from '../../../../../../../controllers/issue-adjustments/hooks/use-current-form-state';
import { useLastProcessedChange } from '../../../../../../../controllers/issue-adjustments/hooks/use-last-change';
import { useViewType } from '../../../../../../../controllers/issue-adjustments/hooks/use-view-type';
import { filterFormDataBasedOnScopes } from '../utils/filter-based-on-scopes/form-data';
import { filterScreenTabsBasedOnReadScopes } from '../utils/filter-based-on-scopes/screen-tabs-data';
import type { ChangeCallback, SubscribeToChanges } from './types';

type UseOnChangeSubscriptionsProps = {
	uiModifications: UiModificationsConfigSingleApp;
	moduleScopes: readonly string[];
};

export const useOnChangeSubscription = ({
	uiModifications,
	moduleScopes,
}: UseOnChangeSubscriptionsProps): SubscribeToChanges => {
	const viewType = useViewType();
	const notifyAppRef = useRef<ChangeCallback | null>(null);
	const { lastProcessedChange } = useLastProcessedChange();
	const { currentFormState, currentScreenTabsState } = useCurrentFormState();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	/**
	 * Single ref for all data that must be up-to-date but not trigger notification.
	 */
	const dataRef = useRef({
		currentFormState,
		currentScreenTabsState,
		uiModifications,
		moduleScopes,
		viewType,
	});
	dataRef.current = {
		currentFormState,
		currentScreenTabsState,
		uiModifications,
		moduleScopes,
		viewType,
	};

	useEffect(() => {
		const notifyApp = notifyAppRef.current;

		if (notifyApp === null || lastProcessedChange === null) {
			return;
		}

		const changeId = lastProcessedChange.changeId;

		notifyApp({
			changedFieldId: lastProcessedChange.fieldId,
			changeId,
			currentFormState: filterFormDataBasedOnScopes(
				dataRef.current.currentFormState,
				dataRef.current.viewType,
				dataRef.current.moduleScopes,
			),
			currentScreenTabsState: filterScreenTabsBasedOnReadScopes(
				dataRef.current.moduleScopes,
				dataRef.current.currentScreenTabsState,
			),
			uiModifications: dataRef.current.uiModifications,
		});
		fireOperationalEvent(createAnalyticsEvent, {
			action: 'success',
			actionSubject: 'jiraUiModifications.appNotification',
			attributes: {
				lifecycleEvent: 'change',
				viewType: dataRef.current.viewType,
			},
		});
	}, [viewType, createAnalyticsEvent, lastProcessedChange]);

	return useCallback<SubscribeToChanges>((callback) => {
		notifyAppRef.current = callback;
	}, []);
};
